import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Avatar,
  Divider,
} from '@mui/material';
import imageLogo from '../assets/images/image.png';
import {
  Work as WorkIcon,
  Inventory as InventoryIcon,
  People as PeopleIcon,
  Android as AndroidIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

interface SidebarProps {
  open: boolean;
  width: number;
  onClose: () => void;
  appbarHeight: number;
  isMobile: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ open, width, onClose, isMobile }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  const isActive = (path: string) => {
    return location.pathname === path || (path !== '/' && location.pathname.startsWith(path));
  };

  const isAdmin = user?.roleId === 1 || String(user?.roleName || '').toLowerCase() === 'admin';

  const mainNavigationItems = [
    { path: '/nominations', label: 'Nominations', icon: <WorkIcon /> },
    { path: '/containers', label: 'Containers', icon: <InventoryIcon /> },
    { path: '/users', label: 'Users', icon: <PeopleIcon /> },
    ...(isAdmin ? [{ path: '/mobile-version', label: 'Mobile APK', icon: <AndroidIcon /> }] : []),
  ];

  const drawerPaperSx = {
    width,
    boxSizing: 'border-box',
    borderRight: '1px solid',
    borderColor: 'divider',
    backgroundColor: '#fff',
    overflowX: 'hidden',
  };

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'persistent'}
      open={open}
      onClose={onClose}
      sx={{
        width: isMobile ? width : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': drawerPaperSx,
      }}
      ModalProps={
        isMobile
          ? {
              keepMounted: true,
            }
          : undefined
      }
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          color: 'text.primary',
        }}
      >
        <Box sx={{ pb: 2 }}>
          <Box sx={{ cursor: 'pointer' }} onClick={() => handleNavigation('/dashboard')}>
            <img
              src={imageLogo}
              alt="SGS Logo"
              style={{
                height: 36,
                width: 'auto',
                display: 'block',
                objectFit: 'contain',
                marginTop: 10,
                marginBottom: 20,
                marginLeft: 16,
              }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
              border: '1px solid',
              borderColor: 'divider',
              mx: 2,
            }}
          >
            <Avatar
              sx={{
                width: 40,
                height: 40,
                background: '#CA4300',
                color: '#fff',
                fontWeight: 600,
              }}
            >
              {user?.firstName?.[0] || user?.lastName?.[0] || 'U'}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {user?.firstName} {user?.lastName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.roleName ?? user?.login}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ mx: 2 }} />

        <Box sx={{ flex: 1, overflow: 'auto', py: 1 }}>
          <List sx={{ px: 2 }}>
            {mainNavigationItems.map((item) => {
              const active = isActive(item.path);
              return (
                <ListItem key={item.path} disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    onClick={() => handleNavigation(item.path)}
                    selected={active}
                    sx={{
                      borderRadius: '12px',
                      py: 1.5,
                      px: 2,
                      background: active ? 'rgba(202, 67, 0, 0.08)' : 'transparent',
                      border: active ? '1px solid rgba(202, 67, 0, 0.35)' : '1px solid transparent',
                      '&:hover': {
                        background: 'rgba(202, 67, 0, 0.06)',
                      },
                      '&.Mui-selected': {
                        background: 'rgba(202, 67, 0, 0.08)',
                        '&:hover': {
                          background: 'rgba(202, 67, 0, 0.12)',
                        },
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 40,
                        color: active ? '#CA4300' : 'inherit',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontWeight: 600,
                        color: active ? '#CA4300' : 'inherit',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
